/**
 * MentionTextarea — textarea con autocompletar de @menciones.
 *
 * Detecta un token `@query` justo antes del cursor, busca perfiles (RPC
 * search_mention_candidates) y muestra un dropdown. Al elegir, inserta
 * `@handle ` reemplazando el token. Navegación con flechas + Enter/Tab,
 * Escape cierra. Si el dropdown está cerrado, delega el onKeyDown al padre
 * (para "Enter envía", etc.).
 *
 * Es controlado: value + onChange(string). Pass-through de placeholder,
 * className, rows, maxLength, autoFocus.
 */
import { useEffect, useRef, useState } from 'react';
import { useMentionSearch, type MentionCandidate } from '@/lib/api/community';
import { cn } from '@/lib/utils';

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
  maxLength?: number;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onFocus?: () => void;
}

// Token de mención activo: '@' + 0..30 chars de handle, al final del texto
// previo al cursor, precedido por inicio o espacio.
const MENTION_RE = /(?:^|\s)@([A-Za-z0-9_\-]{0,30})$/;

export function MentionTextarea({
  value, onChange, placeholder, className, rows = 3, maxLength, autoFocus, onKeyDown, onFocus,
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [query, setQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [active, setActive] = useState(0);
  const pendingCaret = useRef<number | null>(null);

  const { data: candidates = [] } = useMentionSearch(query ?? '');
  const open = query !== null && candidates.length > 0;

  // Reaplica el cursor tras una inserción (value lo controla el padre).
  useEffect(() => {
    if (pendingCaret.current != null && ref.current) {
      const pos = pendingCaret.current;
      pendingCaret.current = null;
      ref.current.focus();
      ref.current.setSelectionRange(pos, pos);
    }
  }, [value]);

  useEffect(() => { setActive(0); }, [query]);

  function recomputeMention(text: string, caret: number) {
    const before = text.slice(0, caret);
    const m = before.match(MENTION_RE);
    if (m) {
      setQuery(m[1] ?? '');
      setMentionStart(caret - (m[1]?.length ?? 0) - 1); // índice del '@'
    } else {
      setQuery(null);
      setMentionStart(null);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const text = e.target.value;
    onChange(text);
    recomputeMention(text, e.target.selectionStart ?? text.length);
  }

  function select(c: MentionCandidate) {
    if (mentionStart == null || !ref.current) return;
    const caret = ref.current.selectionStart ?? value.length;
    const next = value.slice(0, mentionStart) + '@' + c.handle + ' ' + value.slice(caret);
    pendingCaret.current = mentionStart + c.handle.length + 2; // tras "@handle "
    onChange(next);
    setQuery(null);
    setMentionStart(null);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (open) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % candidates.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActive((i) => (i - 1 + candidates.length) % candidates.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const c = candidates[active];
        if (c) { e.preventDefault(); select(c); return; }
      }
      if (e.key === 'Escape') { e.preventDefault(); setQuery(null); setMentionStart(null); return; }
    }
    onKeyDown?.(e);
  }

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => recomputeMention(value, e.currentTarget.selectionStart ?? value.length)}
        onFocus={onFocus}
        onBlur={() => { setTimeout(() => setQuery(null), 120); }}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        autoFocus={autoFocus}
        className={className}
      />
      {open && (
        <ul
          className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-border bg-white p-1 shadow-elevated"
          role="listbox"
        >
          {candidates.map((c, i) => (
            <li key={c.id}>
              <button
                type="button"
                // onMouseDown (no click): dispara antes del blur del textarea.
                onMouseDown={(e) => { e.preventDefault(); select(c); }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  i === active ? 'bg-brand-primary/10' : 'hover:bg-muted',
                )}
              >
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt="" className="h-6 w-6 flex-shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary/15 text-[10px] font-bold text-brand-primary">
                    {(c.full_name ?? c.handle).slice(0, 1).toUpperCase()}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-medium text-brand-text-strong">{c.full_name ?? c.handle}</span>
                  <span className="ml-1.5 text-xs text-muted-foreground">@{c.handle}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
